/** @jsx jsx */

import { Box, Card, CompanyAvatar, Flex, Heading, Paragraph } from "@eworkit/iris";
import { FC } from "react";
import { Link } from "react-router-dom";
import sanitize from "sanitize-html";
import { jsx } from "theme-ui";

import { agoDate } from "@/core/dates";
import { NewsItem } from "@/data/public-profiles";

interface NewsWidgetCardProps {
  news: NewsItem;
  lineClamp: number;
  linkTo: string;
}

export const NewsWidgetCard: FC<NewsWidgetCardProps> = ({ news, lineClamp, linkTo }) => {
  return (
    <Card
      as={Link}
      to={linkTo}
      sx={{
        position: "relative",
        px: [0, 0, 0, 0],
        py: [0, 0, 0, 0],
      }}>
      {news.coverImage.publicUrl && (
        <Box
          sx={{
            borderRadius: "8px 8px 0 0",
            overflow: "hidden",
          }}>
          <img
            src={news.coverImage.publicUrl}
            sx={{
              width: "100%",
              maxHeight: "100%",
            }}
          />
        </Box>
      )}

      <Box p="sm">
        <Flex flexDirection="column" gap="sm">
          <Flex flexDirection="column" gap="xxs">
            <Heading
              size="h-xs"
              sx={{
                lineClamp: lineClamp,
                WebkitLineClamp: lineClamp,
                display: "-webkit-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitBoxOrient: "vertical",
                minHeight: lineClamp * 20,
              }}>
              {news.title}
            </Heading>
            <Paragraph
              sx={{
                lineClamp: lineClamp,
                WebkitLineClamp: lineClamp,
                display: "-webkit-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitBoxOrient: "vertical",
                minHeight: lineClamp * 20,
              }}>
              {news.shortText?.length
                ? news.shortText
                : sanitize(news.longText, { allowedTags: [], allowedAttributes: {} })}
            </Paragraph>
          </Flex>

          <Flex justifyContent="space-between" alignItems="center">
            <Flex gap="xxs" alignItems="center">
              <CompanyAvatar
                size="xs"
                name={news.publicProfile.name}
                img={news.publicProfile.logo.publicUrl ?? undefined}
              />
              <Paragraph size="p-sm" ellipsis sx={{ maxWidth: 150 }}>
                {news.publicProfile.name}
              </Paragraph>
            </Flex>

            <Paragraph size="p-sm" color="n700">
              {agoDate(news.publicationDate)}
            </Paragraph>
          </Flex>
        </Flex>
      </Box>
    </Card>
  );
};
